import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Pagination,
  Popover,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import {
  createMonitorRun,
  listMonitorIncidents,
  listMonitorIssues,
  listMonitorProbes,
  listMonitorRuns,
  listMonitoredBusinesses,
  listWebhookEvents,
} from '../../common/api/scorecardMonitor';
import { getReports } from '../../common/api/reports';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import type {
  ScorecardIncident,
  ScorecardMonitorIssue,
  ScorecardMonitorProbe,
  ScorecardMonitorRun,
  ScorecardMonitoredBusiness,
} from '../../common/types';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import {
  classifyMonitorHealth,
  classifyRunScanState,
  countBy,
  dailyRunStats,
  evidenceFreshness,
  formatDurationMs,
  formatIncidentDuration,
  formatPercent,
  incidentIndustry,
  incidentProvider,
  issueIndustry,
  issueProvider,
  parseRunMetrics,
} from './utils';
import { WebhookFailures } from './WebhookFailures';

export type ScorecardMonitorApi = {
  createMonitorRun: () => Promise<ScorecardMonitorRun>;
  listMonitoredBusinesses: () => Promise<ScorecardMonitoredBusiness[]>;
  listMonitorRuns: () => Promise<ScorecardMonitorRun[]>;
  listMonitorProbes: (runId: number) => Promise<ScorecardMonitorProbe[]>;
  listMonitorIssues: () => Promise<ScorecardMonitorIssue[]>;
  listMonitorIncidents: () => Promise<ScorecardIncident[]>;
  getIndustryByReportId: () => Promise<Map<number, string | null | undefined>>;
  listWebhookEvents: typeof listWebhookEvents;
};

async function defaultGetIndustryByReportId(): Promise<Map<number, string | null | undefined>> {
  // The issues/incidents API keys issues by report_id; the reports list gives
  // us the business_type (industry) per report so we can group issues by
  // industry client-side without a new backend endpoint.
  const data = await getReports(
    { tokens: [], operation: 'and' },
    1,
    100,
    null,
    'report_at',
    'desc',
  );
  const map = new Map<number, string | null | undefined>();
  for (const report of data?.items ?? []) {
    if (typeof report.id === 'number') {
      map.set(report.id, report.business_type);
    }
  }
  return map;
}

const defaultApi: ScorecardMonitorApi = {
  createMonitorRun,
  listMonitoredBusinesses,
  listMonitorRuns,
  listMonitorProbes: (runId) => listMonitorProbes({ run_id: runId }),
  listMonitorIssues: () => listMonitorIssues({}),
  listMonitorIncidents: () => listMonitorIncidents(),
  getIndustryByReportId: defaultGetIndustryByReportId,
  listWebhookEvents,
};

const SEVERITY_COLOR: Record<string, 'red' | 'severity-high' | 'severity-medium'> = {
  SEV1: 'red',
  SEV2: 'severity-high',
  SEV3: 'severity-medium',
};

const SEVERITY_DESCRIPTION: Record<string, string> = {
  SEV1: 'Critical: scorecard data is missing or broken. The dashboard is checked by the daily monitor cadence; stored recheck timestamps are advisory only until an executor enforces them.',
  SEV2: 'Major: source data problems. The dashboard is checked by the daily monitor cadence; stored recheck timestamps are advisory only until an executor enforces them.',
  SEV3: 'Minor: other source problems. The dashboard is checked by the daily monitor cadence; stored recheck timestamps are advisory only until an executor enforces them.',
};

export function SeverityBadge({ severity }: { severity: string }) {
  const color = SEVERITY_COLOR[severity] ?? 'severity-medium';
  const description = SEVERITY_DESCRIPTION[severity];
  const badge = <Badge color={color}>{severity}</Badge>;
  if (!description) return badge;
  return (
    <Popover content={description} dismissButton={false} position="top" size="small">
      {badge}
    </Popover>
  );
}

function runStatusType(status: string): StatusIndicatorProps.Type {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (['dispatching', 'collecting', 'running'].includes(status)) return 'in-progress';
  return 'info';
}

function probeStatusType(status: string): StatusIndicatorProps.Type {
  if (status === 'completed') return 'success';
  if (['failed', 'dispatch_failed', 'timed_out', 'invalid_result'].includes(status)) {
    return 'error';
  }
  if (status === 'degraded') return 'warning';
  if (['planned', 'queued', 'pending'].includes(status)) return 'in-progress';
  return 'info';
}

function issueStatusType(status: string): StatusIndicatorProps.Type {
  if (status === 'resolved') return 'success';
  if (status === 'rechecking') return 'in-progress';
  if (status === 'open') return 'error';
  return 'info';
}

function IncidentTitle({ incident }: { incident: ScorecardIncident }) {
  const summary = incident.details_summary;
  return (
    <SpaceBetween size="xxs">
      <Box fontWeight="bold">{incident.title}</Box>
      {summary && (
        <Box variant="small" color="text-body-secondary">
          {summary}
        </Box>
      )}
    </SpaceBetween>
  );
}

function RecheckCadenceCopy() {
  return (
    <Box variant="small" color="text-body-secondary">
      Severity levels: <SeverityBadge severity="SEV1" /> critical scorecard data ·{' '}
      <SeverityBadge severity="SEV2" /> major source data problems ·{' '}
      <SeverityBadge severity="SEV3" /> minor source problems. Stored recheck timestamps are
      advisory; the executor currently checks monitored businesses on the daily monitor cadence at 5 AM
      Central.
    </Box>
  );
}

function HealthIndicator({ status }: { status: 'online' | 'degraded' | 'offline' | 'unknown' }) {
  if (status === 'online') return <StatusIndicator type="success">Online</StatusIndicator>;
  if (status === 'degraded') return <StatusIndicator type="warning">Degraded</StatusIndicator>;
  if (status === 'offline') return <StatusIndicator type="error">Offline</StatusIndicator>;
  return <StatusIndicator type="pending">No runs yet</StatusIndicator>;
}

function MetricBox({
  label,
  value,
  loading,
  description,
}: {
  label: string;
  value: ReactNode;
  loading?: boolean;
  description?: string;
}) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      {loading ? (
        <Spinner nativeAttributes={{ 'aria-label': `Loading ${label}`, role: 'status' }} />
      ) : (
        <Box fontSize="display-l" fontWeight="bold">
          {value}
        </Box>
      )}
      {description && (
        <Box variant="small" color="text-body-secondary">
          {description}
        </Box>
      )}
    </div>
  );
}

function RunBusinessesCell({
  run,
  activeBusinessCount,
}: {
  run: ScorecardMonitorRun;
  activeBusinessCount?: number;
}) {
  const scanState = classifyRunScanState(run, activeBusinessCount);
  if (!scanState.isZeroScan) return <>{run.businesses_scanned ?? 0}</>;
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={scanState.statusType}>{scanState.label}</StatusIndicator>
      <Box variant="small" color="text-body-secondary">
        {scanState.description}
      </Box>
    </SpaceBetween>
  );
}

function EvidenceCell({
  item,
  latestRun,
}: {
  item: Pick<ScorecardIncident | ScorecardMonitorIssue, 'last_seen_at'>;
  latestRun: ScorecardMonitorRun | null;
}) {
  const freshness = evidenceFreshness(item, latestRun);
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={freshness.statusType}>{freshness.label}</StatusIndicator>
      <Box variant="small" color="text-body-secondary">
        {item.last_seen_at ? `Last verified/collection attempt: ${formatDateStringWithTime(item.last_seen_at)}. ` : ''}
        {freshness.description}
      </Box>
    </SpaceBetween>
  );
}

type IncidentWithDelivery = ScorecardIncident & {
  sentry_event_id?: string | null;
  sentry_sent_at?: string | null;
  sentry_attempt_count?: number | null;
  sentry_last_error_class?: string | null;
};

function IncidentProblemCell({
  incident,
  industryByReportId,
}: {
  incident: ScorecardIncident;
  industryByReportId: Map<number, string | null | undefined>;
}) {
  return (
    <SpaceBetween size="xxs">
      <SeverityBadge severity={incident.severity} />
      <Box variant="small" color="text-body-secondary">
        {incidentProvider(incident)} · {incidentIndustry(incident, industryByReportId)}
      </Box>
      <IncidentTitle incident={incident} />
    </SpaceBetween>
  );
}

function IncidentStateCell({ incident, resolved = false }: { incident: ScorecardIncident; resolved?: boolean }) {
  return (
    <SpaceBetween size="xxs">
      <StatusIndicator type={resolved ? 'success' : 'error'}>
        {resolved ? 'Recently resolved' : `Ongoing · ${formatIncidentDuration(incident)}`}
      </StatusIndicator>
      <Box variant="small" color="text-body-secondary">
        First seen: {incident.first_seen_at ? formatDateStringWithTime(incident.first_seen_at) : '—'}
      </Box>
      <Box variant="small" color="text-body-secondary">
        Last verified: {incident.last_seen_at ? formatDateStringWithTime(incident.last_seen_at) : '—'}
      </Box>
      {resolved && (
        <Box variant="small" color="text-body-secondary">
          Resolved: {incident.resolved_at ? formatDateStringWithTime(incident.resolved_at) : '—'}
        </Box>
      )}
    </SpaceBetween>
  );
}

function SentryDeliveryState({ incident }: { incident: ScorecardIncident }) {
  const delivery = incident as IncidentWithDelivery;
  if (delivery.sentry_event_id || delivery.sentry_sent_at) {
    return <StatusIndicator type="success">Sentry delivered</StatusIndicator>;
  }
  if ((delivery.sentry_attempt_count ?? 0) > 0) {
    return (
      <StatusIndicator type="warning">
        {delivery.sentry_last_error_class
          ? `Sentry retry pending · ${delivery.sentry_last_error_class}`
          : 'Sentry retry pending'}
      </StatusIndicator>
    );
  }
  return <StatusIndicator type="pending">Sentry delivery not reported</StatusIndicator>;
}

function IncidentRecoveryCell({
  incident,
  nextCheckAt,
}: {
  incident: ScorecardIncident;
  nextCheckAt?: string | null;
}) {
  return (
    <SpaceBetween size="xxs">
      <Box>{incident.occurrence_count} occurrences</Box>
      <StatusIndicator type="info">
        {nextCheckAt
          ? `Next scheduled scan: ${formatDateStringWithTime(nextCheckAt)}`
          : 'Daily monitor cadence'}
      </StatusIndicator>
      <SentryDeliveryState incident={incident} />
    </SpaceBetween>
  );
}

type LatestEvidenceRow = {
  incident: ScorecardIncident;
  evidence?: ScorecardMonitorIssue;
};

function CountsTable<T>({
  title,
  description,
  counts,
  renderKey,
  emptyText,
  loading,
}: {
  title: string;
  description?: string;
  counts: Record<string, number>;
  renderKey?: (key: string) => ReactNode;
  emptyText: string;
  loading?: boolean;
}) {
  const items = Object.entries(counts)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
  return (
    <Container
      header={
        <Header variant="h3" description={description} counter={`(${items.length})`}>
          {title}
        </Header>
      }
    >
      {/* variant="embedded" inside a Container matches the established
          dashboard table composition (drip campaign detail, chain detail,
          voice test-call dashboard). Putting variant="container"/"stacked"
          directly on the Table renders the table flush to its own card
          edge, which produced the zero-padding look that was rejected. */}
      <Table
        variant="embedded"
        columnDefinitions={[
          {
            id: 'key',
            header: 'Group',
            cell: (item: { key: string; count: number }) =>
              renderKey ? renderKey(item.key) : item.key,
          },
          {
            id: 'count',
            header: 'Open issues',
            cell: (item: { key: string; count: number }) => item.count,
          },
        ]}
        items={items}
        loading={loading}
        loadingText="Loading…"
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {emptyText}
          </Box>
        }
      />
    </Container>
  );
}

export type ScorecardMonitorDashboardProps = {
  api?: Partial<ScorecardMonitorApi>;
  now?: Date;
};

export default function ScorecardMonitorDashboard({
  api,
  now,
}: ScorecardMonitorDashboardProps = {}) {
  const resolvedApi: ScorecardMonitorApi = { ...defaultApi, ...api };
  const queryClient = useQueryClient();

  const businessesQuery = useQuery({
    queryKey: ['scorecard-monitor', 'businesses'],
    queryFn: resolvedApi.listMonitoredBusinesses,
    refetchInterval: 60_000,
  });
  const runsQuery = useQuery({
    queryKey: ['scorecard-monitor', 'runs'],
    queryFn: resolvedApi.listMonitorRuns,
    refetchInterval: 60_000,
  });
  const issuesQuery = useQuery({
    queryKey: ['scorecard-monitor', 'issues'],
    queryFn: resolvedApi.listMonitorIssues,
    refetchInterval: 60_000,
  });
  const incidentsQuery = useQuery({
    queryKey: ['scorecard-monitor', 'incidents'],
    queryFn: resolvedApi.listMonitorIncidents,
    refetchInterval: 60_000,
  });
  const industryQuery = useQuery({
    queryKey: ['scorecard-monitor', 'report-industries'],
    queryFn: resolvedApi.getIndustryByReportId,
    staleTime: 600_000,
  });

  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);
  const issues = useMemo(() => issuesQuery.data ?? [], [issuesQuery.data]);
  const incidents = useMemo(() => incidentsQuery.data ?? [], [incidentsQuery.data]);
  const businesses = useMemo(() => businessesQuery.data ?? [], [businessesQuery.data]);
  const activeBusinesses = useMemo(() => businesses.filter((business) => business.active), [businesses]);
  const runMutation = useMutation({
    mutationFn: resolvedApi.createMonitorRun,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['scorecard-monitor', 'runs'] }),
        queryClient.invalidateQueries({ queryKey: ['scorecard-monitor', 'probes'] }),
        queryClient.invalidateQueries({ queryKey: ['scorecard-monitor', 'issues'] }),
        queryClient.invalidateQueries({ queryKey: ['scorecard-monitor', 'incidents'] }),
      ]);
    },
  });

  const health = useMemo(
    () => classifyMonitorHealth(runs, now, activeBusinesses.length),
    [runs, now, activeBusinesses.length],
  );
  const latestRunScanState = useMemo(
    () =>
      health.latestRun ? classifyRunScanState(health.latestRun, activeBusinesses.length) : null,
    [health.latestRun, activeBusinesses.length],
  );
  const daily = useMemo(() => dailyRunStats(runs), [runs]);
  const latestMetrics = useMemo(
    () => parseRunMetrics(health.latestRun?.metrics_json),
    [health.latestRun],
  );
  const latestRunId = health.latestRun?.id;
  const probesQuery = useQuery({
    queryKey: ['scorecard-monitor', 'probes', latestRunId],
    queryFn: () =>
      typeof latestRunId === 'number'
        ? resolvedApi.listMonitorProbes(latestRunId)
        : Promise.resolve([]),
    enabled: typeof latestRunId === 'number',
    refetchInterval: 30_000,
  });
  const probes = useMemo(() => probesQuery.data ?? [], [probesQuery.data]);
  const { items: visibleProbes, paginationProps: probePaginationProps } = useCollection(probes, {
    pagination: { pageSize: 20 },
  });

  const openIssues = useMemo(() => issues.filter((issue) => issue.status !== 'resolved'), [issues]);
  const openIncidents = useMemo(
    () => incidents.filter((incident) => incident.ongoing || incident.status === 'open'),
    [incidents],
  );
  const historyIncidents = useMemo(
    () => incidents.filter((incident) => !(incident.ongoing || incident.status === 'open')),
    [incidents],
  );
  const industryByReportId = useMemo(
    () => industryQuery.data ?? new Map<number, string | null | undefined>(),
    [industryQuery.data],
  );
  const businessById = useMemo(
    () => new Map(businesses.map((business) => [business.id, business])),
    [businesses],
  );
  const latestEvidenceRows = useMemo<LatestEvidenceRow[]>(
    () =>
      openIncidents.map((incident) => {
        const evidence = openIssues
          .filter((issue) => issue.incident_id === incident.id)
          .sort(
            (a, b) =>
              new Date(b.last_seen_at ?? 0).getTime() - new Date(a.last_seen_at ?? 0).getTime(),
          )[0];
        return { incident, evidence };
      }),
    [openIncidents, openIssues],
  );

  const issuesBySeverity = useMemo(
    () => countBy(openIssues, (issue) => issue.severity),
    [openIssues],
  );
  const issuesByProvider = useMemo(
    () => countBy(openIssues, (issue) => issueProvider(issue)),
    [openIssues],
  );
  const issuesByIndustry = useMemo(
    () => countBy(openIssues, (issue) => issueIndustry(issue, industryByReportId)),
    [openIssues, industryByReportId],
  );
  const anyError =
    businessesQuery.error ||
    runsQuery.error ||
    probesQuery.error ||
    issuesQuery.error ||
    incidentsQuery.error;

  const sev1 = issuesBySeverity['SEV1'] ?? 0;
  const sev2 = issuesBySeverity['SEV2'] ?? 0;
  const sev3 = issuesBySeverity['SEV3'] ?? 0;

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Scorecards', href: '/dashboard/scorecard' },
        { text: 'Monitoring', href: '/dashboard/scorecard/monitor' },
      ]}
      headerVariant="high-contrast"
      header={
        <Container
          header={
            <Header
              variant="h1"
              description="Daily automated health checks of monitored scorecard businesses: run success, per-source timings, and open data issues."
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    iconName="refresh"
                    loading={runMutation.isPending}
                    onClick={() => runMutation.mutate()}
                  >
                    Run full check now
                  </Button>
                  <HealthIndicator status={health.status} />
                </SpaceBetween>
              }
            >
              Scorecard System Monitoring
            </Header>
          }
        >
          <SpaceBetween size="m">
            {anyError && (
              <Alert type="error" header="Failed to load monitoring data">
                {String(anyError)}
              </Alert>
            )}
            {runMutation.error && (
              <Alert type="error" header="Failed to start monitor run">
                {String(runMutation.error)}
              </Alert>
            )}
            {(health.status === 'offline' || health.status === 'degraded') && (
              <Alert
                type={health.status === 'offline' ? 'error' : 'warning'}
                header={
                  health.status === 'offline'
                    ? 'Monitoring appears offline'
                    : 'Monitoring is degraded'
                }
              >
                {health.reason} Inspect the latest source probes and watchdog state before treating
                the scorecard data as verified.
              </Alert>
            )}
            {latestRunScanState?.isZeroScan && health.status !== 'degraded' && (
              <Alert type="info" header="Latest run scanned 0 businesses">
                {latestRunScanState.description}
              </Alert>
            )}
            <ColumnLayout columns={4} variant="text-grid">
              <MetricBox
                label="Monitored businesses"
                loading={businessesQuery.isPending}
                value={activeBusinesses.length}
                description={`${businesses.length - activeBusinesses.length} inactive`}
              />
              <MetricBox
                label="Last run"
                loading={runsQuery.isPending}
                value={
                  health.latestRun ? (
                    <StatusIndicator type={runStatusType(health.latestRun.status ?? 'unknown')}>
                      {health.latestRun.status}
                    </StatusIndicator>
                  ) : (
                    '—'
                  )
                }
                description={
                  health.latestRun?.started_at
                    ? formatDateStringWithTime(health.latestRun.started_at)
                    : 'No runs recorded'
                }
              />
              <MetricBox
                label="Open issues"
                loading={issuesQuery.isPending}
                value={openIssues.length}
                description={`${sev1} SEV1 · ${sev2} SEV2 · ${sev3} SEV3`}
              />
              <MetricBox
                label="Current incidents"
                loading={incidentsQuery.isPending}
                value={openIncidents.length}
                description="Unresolved scorecard problems"
              />
            </ColumnLayout>
            <RecheckCadenceCopy />
          </SpaceBetween>
        </Container>
      }
    >
      <SpaceBetween size="l">
        <WebhookFailures loadEvents={resolvedApi.listWebhookEvents} />
        <Grid
          gridDefinition={[{ colspan: { default: 12, m: 6 } }, { colspan: { default: 12, m: 6 } }]}
        >
          <Container
            header={
              <Header
                variant="h2"
                description="One row per day; success rate counts completed vs failed runs."
                counter={`(${daily.length})`}
              >
                Daily run success
              </Header>
            }
          >
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'date', header: 'Date (UTC)', cell: (item) => item.date },
                {
                  id: 'success',
                  header: 'Success rate',
                  cell: (item) =>
                    item.successRate === null ? (
                      <StatusIndicator type="in-progress">running</StatusIndicator>
                    ) : (
                      <StatusIndicator type={item.successRate >= 1 ? 'success' : 'error'}>
                        {formatPercent(item.successRate)}
                      </StatusIndicator>
                    ),
                },
                { id: 'completed', header: 'Completed', cell: (item) => item.completed },
                {
                  id: 'zero_scans',
                  header: '0-scan completed',
                  cell: (item) =>
                    item.zeroBusinessCompleted > 0 ? (
                      <StatusIndicator type="warning">{item.zeroBusinessCompleted}</StatusIndicator>
                    ) : (
                      '0'
                    ),
                },
                { id: 'failed', header: 'Failed', cell: (item) => item.failed },
                { id: 'total', header: 'Runs', cell: (item) => item.total },
              ]}
              items={daily}
              loading={runsQuery.isPending}
              loadingText="Loading runs…"
              empty={
                <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                  No monitor runs yet.
                </Box>
              }
            />
          </Container>
          <Container
            header={
              <Header
                variant="h2"
                description={
                  health.latestRun?.started_at
                    ? `Fresh source probes from the latest run (${formatDateStringWithTime(health.latestRun.started_at)}).`
                    : 'No completed runs yet.'
                }
                counter={`(${latestMetrics.sources.length})`}
              >
                Timings and health per source
              </Header>
            }
            footer={
              health.latestRun ? (
                <Box variant="small" color="text-body-secondary">
                  Overall: {health.latestRun.businesses_scanned ?? 0} businesses scanned ·{' '}
                  {health.latestRun.reports_scanned ?? 0} reports ·{' '}
                  {health.latestRun.healthy_reports ?? 0} healthy ·{' '}
                  {health.latestRun.issues_found ?? 0} issues · {latestMetrics.recrawls_queued ?? 0}{' '}
                  re-crawls queued · total {formatDurationMs(health.latestRun.duration_ms)}
                  {latestRunScanState?.isZeroScan ? ` · ${latestRunScanState.label}` : ''}
                </Box>
              ) : undefined
            }
          >
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'source', header: 'Source', cell: (item) => item.source },
                { id: 'attempted', header: 'Attempted', cell: (item) => item.attempted },
                {
                  id: 'issues',
                  header: 'Issues found',
                  cell: (item) =>
                    item.issues > 0 ? (
                      <StatusIndicator type="error">{item.issues}</StatusIndicator>
                    ) : (
                      <StatusIndicator type="success">0</StatusIndicator>
                    ),
                },
                { id: 'success', header: 'Complete', cell: (item) => item.success },
                {
                  id: 'unhealthy',
                  header: 'Failed / degraded / pending',
                  cell: (item) => `${item.failed} / ${item.degraded} / ${item.pending}`,
                },
                {
                  id: 'duration',
                  header: 'Probe duration',
                  cell: (item) => formatDurationMs(item.duration_ms),
                },
              ]}
              items={latestMetrics.sources}
              loading={runsQuery.isPending}
              loadingText="Loading timings…"
              empty={
                <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                  No per-source metrics recorded yet.
                </Box>
              }
            />
          </Container>
        </Grid>

        <Container
          header={
            <Header
              variant="h2"
              counter={`(${probes.length})`}
              description="One durable row per business/source in the latest run. A run is not complete until every row is terminal or the watchdog times it out."
            >
              Latest source probes
            </Header>
          }
        >
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'source',
                header: 'Source',
                cell: (item: ScorecardMonitorProbe) => (
                  <SpaceBetween size="xxs">
                    <Box fontWeight="bold">{item.source}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {item.required ? 'Required' : 'Optional'} · {item.provider ?? 'Unknown provider'}
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'status',
                header: 'Terminal state',
                cell: (item: ScorecardMonitorProbe) => (
                  <StatusIndicator type={probeStatusType(item.status)}>{item.status}</StatusIndicator>
                ),
              },
              {
                id: 'business',
                header: 'Business / report',
                cell: (item: ScorecardMonitorProbe) => (
                  <SpaceBetween size="xxs">
                    <Box>Business {item.monitored_business_id ?? '—'}</Box>
                    <Box variant="small" color="text-body-secondary">
                      Report {item.report_id ?? '—'} · task {item.task_id ?? '—'}
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'counts',
                header: 'Raw / unique / previous / period / lifetime',
                cell: (item: ScorecardMonitorProbe) =>
                  `${item.raw_review_count ?? '—'} / ${item.unique_review_count ?? '—'} / ${item.previous_raw_review_count ?? '—'} / ${item.period_review_count ?? '—'} / ${item.lifetime_review_count ?? '—'}`,
              },
              {
                id: 'timing',
                header: 'Queue / run duration',
                cell: (item: ScorecardMonitorProbe) =>
                  `${formatDurationMs(item.queue_latency_ms)} / ${formatDurationMs(item.duration_ms)}`,
              },
              {
                id: 'contract',
                header: 'Contract',
                cell: (item: ScorecardMonitorProbe) => (
                  <SpaceBetween size="xxs">
                    <Box>
                      {item.collection_complete === true
                        ? 'Complete'
                        : item.collection_complete === false
                          ? 'Incomplete'
                          : 'No result'}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                      Identity {item.identity_status ?? '—'} · response metadata{' '}
                      {item.response_metadata_status ?? '—'}
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'evidence',
                header: 'Collection evidence',
                cell: (item: ScorecardMonitorProbe) => (
                  <SpaceBetween size="xxs">
                    <Box>
                      {item.completed_at
                        ? formatDateStringWithTime(item.completed_at)
                        : item.snapshot_collected_at
                          ? formatDateStringWithTime(item.snapshot_collected_at)
                          : 'Not terminal'}
                    </Box>
                    {(item.error_category || item.error_class) && (
                      <Box variant="small" color="text-status-error">
                        {item.error_category ?? item.error_class}
                      </Box>
                    )}
                  </SpaceBetween>
                ),
              },
            ]}
            items={visibleProbes}
            trackBy="uuid"
            pagination={<Pagination {...probePaginationProps} />}
            loading={probesQuery.isPending && typeof latestRunId === 'number'}
            loadingText="Loading source probes…"
            empty={
              <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                {health.latestRun
                  ? 'No durable probe rows were recorded for this run.'
                  : 'No monitor run is available.'}
              </Box>
            }
          />
        </Container>

        <Grid
          gridDefinition={[
            { colspan: { default: 12, m: 4 } },
            { colspan: { default: 12, m: 4 } },
            { colspan: { default: 12, m: 4 } },
          ]}
        >
          <CountsTable
            title="Open issues by severity"
            counts={issuesBySeverity}
            renderKey={(key) => <SeverityBadge severity={key} />}
            emptyText="No open issues."
            loading={issuesQuery.isPending}
          />
          <CountsTable
            title="Open issues by provider"
            counts={issuesByProvider}
            emptyText="No open issues."
            loading={issuesQuery.isPending}
          />
          <CountsTable
            title="Open issues by industry"
            description="Industry of the affected scorecard (business type)."
            counts={issuesByIndustry}
            emptyText="No open issues."
            loading={issuesQuery.isPending || industryQuery.isPending}
          />
        </Grid>

        <Container
          header={
            <Header
              variant="h2"
              counter={`(${latestEvidenceRows.length})`}
              description="Most recent verification for each current incident. Earlier checks remain in incident history."
            >
              Latest evidence
            </Header>
          }
        >
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'problem',
                header: 'Problem',
                cell: (item: LatestEvidenceRow) => (
                  <IncidentProblemCell
                    incident={item.incident}
                    industryByReportId={industryByReportId}
                  />
                ),
              },
              {
                id: 'verification',
                header: 'Verification',
                cell: (item: LatestEvidenceRow) =>
                  item.evidence ? (
                    <SpaceBetween size="xxs">
                      <StatusIndicator type={issueStatusType(item.evidence.status ?? 'open')}>
                        {item.evidence.status}
                      </StatusIndicator>
                      <EvidenceCell item={item.evidence} latestRun={health.latestRun} />
                    </SpaceBetween>
                  ) : (
                    <StatusIndicator type="pending">No per-run evidence reported</StatusIndicator>
                  ),
              },
              {
                id: 'finding',
                header: 'Finding',
                cell: (item: LatestEvidenceRow) => (
                  <SpaceBetween size="xxs">
                    <Box fontWeight="bold">{item.evidence?.issue_type ?? item.incident.issue_type}</Box>
                    {(item.evidence?.details_summary || item.incident.details_summary) && (
                      <Box variant="small" color="text-body-secondary">
                        {item.evidence?.details_summary ?? item.incident.details_summary}
                      </Box>
                    )}
                  </SpaceBetween>
                ),
              },
            ]}
            items={latestEvidenceRows}
            loading={issuesQuery.isPending || incidentsQuery.isPending}
            loadingText="Loading latest evidence…"
            empty={
              <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                <b>No current incident evidence</b>
                <Box color="inherit" variant="p">
                  No unresolved scorecard incidents are open right now.
                </Box>
              </Box>
            }
          />
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              counter={`(${openIncidents.length})`}
              description="One row per unresolved scorecard problem. Duration starts when it was first detected."
            >
              Current incidents
            </Header>
          }
        >
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'problem',
                header: 'Problem',
                cell: (item: ScorecardIncident) => (
                  <IncidentProblemCell incident={item} industryByReportId={industryByReportId} />
                ),
              },
              {
                id: 'state',
                header: 'State',
                cell: (item: ScorecardIncident) => <IncidentStateCell incident={item} />,
              },
              {
                id: 'recovery',
                header: 'Recovery',
                cell: (item: ScorecardIncident) => (
                  <IncidentRecoveryCell
                    incident={item}
                    nextCheckAt={businessById.get(item.monitored_business_id ?? Number.NaN)?.next_check_at}
                  />
                ),
              },
            ]}
            items={openIncidents}
            loading={incidentsQuery.isPending}
            loadingText="Loading current incidents…"
            empty={
              <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                <b>No current incidents</b>
                <Box color="inherit" variant="p">
                  No unresolved monitor incidents are open right now.
                </Box>
              </Box>
            }
          />
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              counter={`(${historyIncidents.length})`}
              description="Resolved scorecard incidents, newest first."
            >
              Recently resolved
            </Header>
          }
        >
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'problem',
                header: 'Problem',
                cell: (item: ScorecardIncident) => (
                  <IncidentProblemCell incident={item} industryByReportId={industryByReportId} />
                ),
              },
              {
                id: 'state',
                header: 'State',
                cell: (item: ScorecardIncident) => <IncidentStateCell incident={item} resolved />,
              },
              {
                id: 'outcome',
                header: 'Outcome',
                cell: (item: ScorecardIncident) => (
                  <SpaceBetween size="xxs">
                    <Box>{formatIncidentDuration(item)}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {item.occurrence_count} occurrences
                    </Box>
                  </SpaceBetween>
                ),
              },
            ]}
            items={historyIncidents}
            loading={incidentsQuery.isPending}
            loadingText="Loading recently resolved incidents…"
            empty={
              <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                <b>No recently resolved incidents</b>
                <Box color="inherit" variant="p">
                  No resolved monitor incidents have been recorded yet.
                </Box>
              </Box>
            }
          />
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              counter={`(${runs.length})`}
              description="Most recent monitor runs (max 100)."
            >
              Recent runs
            </Header>
          }
        >
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'run',
                header: 'Run',
                cell: (item: ScorecardMonitorRun) => (
                  <SpaceBetween size="xxs">
                    <Box>
                      {item.started_at ? formatDateStringWithTime(item.started_at) : '—'}
                    </Box>
                    <StatusIndicator type={runStatusType(item.status ?? 'unknown')}>
                      {item.status}
                    </StatusIndicator>
                    <Box variant="small" color="text-body-secondary">
                      Mode: {item.mode}
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'coverage',
                header: 'Coverage',
                cell: (item: ScorecardMonitorRun) => (
                  <SpaceBetween size="xxs">
                    <RunBusinessesCell run={item} activeBusinessCount={activeBusinesses.length} />
                    <Box variant="small" color="text-body-secondary">
                      {item.healthy_reports ?? 0} healthy · {item.issues_found ?? 0} issues
                    </Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'result',
                header: 'Result',
                cell: (item: ScorecardMonitorRun) => (
                  <SpaceBetween size="xxs">
                    <Box>{formatDurationMs(item.duration_ms)}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {item.error_message ?? 'No error reported'}
                    </Box>
                  </SpaceBetween>
                ),
              },
            ]}
            items={runs}
            loading={runsQuery.isPending}
            loadingText="Loading runs…"
            empty={
              <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
                <b>No monitor runs yet</b>
                <Box color="inherit" variant="p">
                  The daily monitor has not reported any runs.
                </Box>
              </Box>
            }
          />
        </Container>
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
